import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { SlidersHorizontal, X, Search, Check } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { trackPageView } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'Única'];
const COLORS = ['Negro', 'Blanco', 'Dorado', 'Verde', 'Coral', 'Natural'];
const COLOR_MAP = {
  'Negro': '#000000', 'Blanco': '#FFFFFF', 'Dorado': '#C9A962',
  'Verde': '#2D5A27', 'Coral': '#FF6F61', 'Natural': '#E8D5B7',
};
const SORT_OPTIONS = [
  { value: 'created_at-desc', label: 'Recientes' },
  { value: 'price-asc', label: 'Menor precio' },
  { value: 'price-desc', label: 'Mayor precio' },
  { value: 'name-asc', label: 'A — Z' },
];

export default function ShopPage() {
  const { category: categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const PRODUCTS_PER_PAGE = 16;

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('created_at-desc');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  useEffect(() => { trackPageView('/tienda', 'Tienda'); }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories?status=active`);
        setCategories(res.data);
        if (categorySlug) {
          const cat = res.data.find(c => c.slug === categorySlug);
          if (cat) setSelectedCategory(cat.id);
        }
      } catch (e) {}
    };
    fetchCategories();
  }, [categorySlug]);

  useEffect(() => {
    setPage(0); setProducts([]); setHasMore(true);
  }, [selectedCategory, selectedSizes, selectedColors, priceRange, sortBy, searchQuery]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (page === 0) setLoading(true); else setLoadingMore(true);
      try {
        const [sf, so] = sortBy.split('-');
        const p = new URLSearchParams();
        p.append('status', 'active'); p.append('sort_by', sf); p.append('sort_order', so);
        p.append('skip', (page * PRODUCTS_PER_PAGE).toString()); p.append('limit', PRODUCTS_PER_PAGE.toString());
        if (selectedCategory) p.append('category_id', selectedCategory);
        if (selectedSizes.length) p.append('size', selectedSizes[0]);
        if (selectedColors.length) p.append('color', selectedColors[0]);
        if (priceRange.min) p.append('min_price', priceRange.min);
        if (priceRange.max) p.append('max_price', priceRange.max);
        if (searchQuery) p.append('search', searchQuery);
        const [pr, cr] = await Promise.all([axios.get(`${API}/products?${p}`), axios.get(`${API}/products/count?${p}`)]);
        if (page === 0) setProducts(pr.data); else setProducts(prev => [...prev, ...pr.data]);
        setTotalCount(cr.data.count);
        setHasMore(pr.data.length === PRODUCTS_PER_PAGE && (page + 1) * PRODUCTS_PER_PAGE < cr.data.count);
      } catch (e) {} finally { setLoading(false); setLoadingMore(false); }
    };
    fetchProducts();
  }, [page, selectedCategory, selectedSizes, selectedColors, priceRange, sortBy, searchQuery]);

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    const cat = categories.find(c => c.id === catId);
    if (cat) navigate(`/tienda/${cat.slug}`); else navigate('/tienda');
  };
  const toggleSize = (s) => setSelectedSizes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleColor = (c) => setSelectedColors(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const clearFilters = () => { setSelectedCategory(''); setSelectedSizes([]); setSelectedColors([]); setPriceRange({ min: '', max: '' }); setSearchQuery(''); setSearchInput(''); navigate('/tienda'); };
  const activeFilterCount = (selectedCategory ? 1 : 0) + selectedSizes.length + selectedColors.length + (priceRange.min ? 1 : 0) + (priceRange.max ? 1 : 0);
  const currentCategory = categories.find(c => c.id === selectedCategory);

  const observerTarget = useRef(null);
  const loadMore = useCallback(() => { if (!loadingMore && hasMore && !loading) setPage(p => p + 1); }, [loadingMore, hasMore, loading]);
  useEffect(() => {
    const obs = new IntersectionObserver((e) => { if (e[0].isIntersecting && hasMore && !loadingMore && !loading) loadMore(); }, { threshold: 0.1 });
    const t = observerTarget.current;
    if (t) obs.observe(t);
    return () => { if (t) obs.unobserve(t); };
  }, [hasMore, loadingMore, loading, loadMore]);

  const handleSearch = (e) => { e.preventDefault(); setSearchQuery(searchInput); };

  return (
    <div className="min-h-screen bg-black pt-[72px]" data-testid="shop-page">

      {/* Sticky toolbar */}
      <div className="sticky top-[72px] z-20 bg-black/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">

          {/* Search */}
          <div className="py-3">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="¿Qué estás buscando?"
                className={`w-full h-10 pl-10 pr-4 bg-white/[0.03] rounded-full text-white text-sm placeholder-white/20 focus:outline-none transition-all duration-300 ${
                  searchFocused ? 'bg-white/[0.06] ring-1 ring-white/10' : ''
                }`}
              />
              {searchInput && (
                <button type="button" onClick={() => { setSearchInput(''); setSearchQuery(''); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-4 h-4 text-white/30" />
                </button>
              )}
            </form>
          </div>

          {/* Categories */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-3 -mx-4 px-4 md:mx-0 md:px-0">
            <button
              onClick={() => handleCategoryChange('')}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all duration-300 ${
                !selectedCategory
                  ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.1)]'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              Todo
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all duration-300 whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.1)]'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Filter bar */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <motion.button
              onClick={() => setShowFilters(!showFilters)}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium transition-all duration-300 ${
                showFilters || activeFilterCount > 0
                  ? 'bg-white text-black'
                  : 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08] hover:text-white/60'
              }`}
            >
              <SlidersHorizontal className="w-3 h-3" />
              {activeFilterCount > 0 ? `${activeFilterCount} filtro${activeFilterCount > 1 ? 's' : ''}` : 'Filtrar'}
            </motion.button>

            {searchQuery && (
              <span className="flex items-center gap-1 h-8 px-3 rounded-full bg-gold/10 text-gold text-xs">
                "{searchQuery}"
                <button onClick={() => { setSearchQuery(''); setSearchInput(''); }}><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-white/15 text-xs mr-1">{totalCount}</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`hidden md:block px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                  sortBy === opt.value ? 'bg-white/10 text-white/70' : 'text-white/20 hover:text-white/40'
                }`}
              >
                {opt.label}
              </button>
            ))}
            {/* Mobile sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="md:hidden bg-transparent text-white/40 text-xs border-none outline-none appearance-none cursor-pointer"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-black">{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              className="overflow-hidden"
            >
              <div className="bg-white/[0.02] rounded-2xl p-5 mb-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Sizes */}
                <div>
                  <span className="text-white/30 text-[11px] uppercase tracking-widest mb-3 block">Talla</span>
                  <div className="flex flex-wrap gap-1.5">
                    {SIZES.map((size) => (
                      <motion.button
                        key={size}
                        whileTap={{ scale: 0.92 }}
                        onClick={() => toggleSize(size)}
                        className={`h-9 px-3.5 rounded-full text-xs font-medium transition-all duration-200 ${
                          selectedSizes.includes(size)
                            ? 'bg-white text-black'
                            : 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08] border border-white/[0.06]'
                        }`}
                      >
                        {size}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Colors */}
                <div>
                  <span className="text-white/30 text-[11px] uppercase tracking-widest mb-3 block">Color</span>
                  <div className="flex flex-wrap gap-3">
                    {COLORS.map((color) => {
                      const isSelected = selectedColors.includes(color);
                      const isLight = ['Blanco', 'Natural', 'Dorado'].includes(color);
                      return (
                        <motion.button
                          key={color}
                          whileTap={{ scale: 0.85 }}
                          onClick={() => toggleColor(color)}
                          className={`relative w-8 h-8 rounded-full transition-all duration-200 ${
                            isSelected ? 'ring-[2px] ring-offset-[3px] ring-offset-black ring-white scale-110' : 'hover:scale-110'
                          }`}
                          style={{
                            backgroundColor: COLOR_MAP[color] || '#888',
                            border: isLight && !isSelected ? '1px solid rgba(255,255,255,0.15)' : 'none'
                          }}
                          title={color}
                        >
                          {isSelected && <Check className={`w-3 h-3 absolute inset-0 m-auto ${isLight ? 'text-black' : 'text-white'}`} />}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Price */}
                <div>
                  <span className="text-white/30 text-[11px] uppercase tracking-widest mb-3 block">Precio</span>
                  <div className="flex gap-2 items-center">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/15 text-xs">$</span>
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                        className="w-full h-9 pl-7 pr-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white text-xs placeholder-white/15 focus:outline-none focus:border-white/15"
                      />
                    </div>
                    <span className="text-white/10">—</span>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/15 text-xs">$</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                        className="w-full h-9 pl-7 pr-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white text-xs placeholder-white/15 focus:outline-none focus:border-white/15"
                      />
                    </div>
                  </div>
                  {activeFilterCount > 0 && (
                    <button onClick={clearFilters} className="mt-4 text-[11px] text-white/25 hover:text-white/50 transition-colors underline underline-offset-2">
                      Limpiar todo
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active filter pills - when panel is closed */}
        {activeFilterCount > 0 && !showFilters && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap gap-1.5 pb-4"
          >
            {currentCategory && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/[0.04] text-white/50 text-[11px] rounded-full">
                {currentCategory.name}
                <button onClick={() => handleCategoryChange('')} className="hover:text-white"><X className="w-2.5 h-2.5" /></button>
              </span>
            )}
            {selectedSizes.map(s => (
              <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/[0.04] text-white/50 text-[11px] rounded-full">
                {s}<button onClick={() => toggleSize(s)} className="hover:text-white"><X className="w-2.5 h-2.5" /></button>
              </span>
            ))}
            {selectedColors.map(c => (
              <span key={c} className="inline-flex items-center gap-1 pl-1.5 pr-2.5 py-1 bg-white/[0.04] text-white/50 text-[11px] rounded-full">
                <span className="w-3 h-3 rounded-full mr-0.5" style={{ backgroundColor: COLOR_MAP[c] }} />{c}
                <button onClick={() => toggleColor(c)} className="hover:text-white ml-0.5"><X className="w-2.5 h-2.5" /></button>
              </span>
            ))}
            <button onClick={clearFilters} className="px-2.5 py-1 text-white/20 text-[11px] hover:text-white/40 transition-colors">
              Limpiar
            </button>
          </motion.div>
        )}

        {/* Products */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 pb-16">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.05 }}
                className="space-y-3"
              >
                <div className="aspect-[3/4] rounded-2xl bg-white/[0.03] animate-pulse" />
                <div className="h-3 bg-white/[0.03] rounded-full w-3/4 animate-pulse" />
                <div className="h-3 bg-white/[0.03] rounded-full w-1/3 animate-pulse" />
              </motion.div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-32"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/[0.03] flex items-center justify-center">
              <Search className="w-6 h-6 text-white/15" />
            </div>
            <p className="text-white/30 text-base mb-1">Sin resultados</p>
            <p className="text-white/15 text-sm mb-6">Probá con otros filtros o categorías</p>
            <button onClick={clearFilters} className="text-gold text-sm hover:text-gold-hover transition-colors">
              Ver todos los productos
            </button>
          </motion.div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 pb-8">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index % PRODUCTS_PER_PAGE} />
              ))}
            </div>

            {hasMore && (
              <div ref={observerTarget} className="py-12 flex justify-center">
                {loadingMore && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center gap-3"
                  >
                    <div className="flex gap-1">
                      {[0, 1, 2].map(i => (
                        <motion.div
                          key={i}
                          className="w-1.5 h-1.5 bg-gold/50 rounded-full"
                          animate={{ y: [0, -8, 0] }}
                          transition={{ duration: 0.6, delay: i * 0.15, repeat: Infinity }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
